import { useMemo } from 'react';
import { RSOS_PLACE_SOURCE } from '@rsos/maps/interface/rsosGeocoder/rsosGeocoder';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import {
  CATEGORY_PRIORITY_ORDER,
  FIXED_CATEGORY_LABELS,
  MAX_POI,
} from './GlobalMapSearch.constants';
import { resolveCategoryIcon } from './GlobalMapSearch.icons';
import { isGlobalPlaceService } from './GlobalMapSearch.poi';
import type {
  MapCenter,
  SearchResultItem,
  TalkJSMessageSource,
} from './GlobalMapSearch.types';
import {
  formatDistance,
  getFeatureLabel,
  getFeatureSublabel,
  getSpatialObjectCenter,
  haversineDistance,
  PlaceResultDedupe,
} from './GlobalMapSearch.utils';

/** Display label for each message-source variant in the dropdown sublabel. */
const MESSAGE_SOURCE_LABELS: Record<TalkJSMessageSource, string> = {
  tf911: 'Text-from-911',
  collaborator: 'Collaborator',
  agencyShare: 'Agency Share',
};

export interface PreparedItem {
  item: SearchResultItem;
  label: string;
  sublabel: string;
  distanceLabel: string;
  flatIdx: number;
  icon:
    | 'axon'
    | 'place'
    | 'what3words'
    | 'gis'
    | 'static'
    | 'dynamic'
    | 'call'
    | 'transcript'
    | 'message'
    | 'alert'
    | 'rade-maf'
    | 'rade-adr';
  /**
   * Bundled URL for a category-specific silhouette icon resolved from
   * the layer/sublayer title (see `GlobalMapSearch.icons.ts`). When
   * absent, the dropdown falls back to the generic icon-by-type map.
   */
  iconUrl?: string;
  distance: number;
  /**
   * Text match score (0-1). Higher = better match to the search query.
   * Used to prioritize exact/prefix matches over distance-only results.
   */
  textMatchScore: number;
}

export interface ResultGroup {
  category: string;
  heading: string;
  items: PreparedItem[];
}

/**
 * Calculate a text match score (0-1) for how well a result label matches
 * the search query. Higher = better match.
 *
 * Scoring:
 *   1.0 = exact match (case-insensitive)
 *   0.9 = label starts with query (prefix match)
 *   0.8 = query starts with label (query is more specific)
 *   0.7 = all query words found in label
 *   0.5 = some query words found in label
 *   0.0 = no significant match
 */
function calculateTextMatchScore(
  label: string,
  sublabel: string,
  query: string,
): number {
  if (!query || !label) return 0;

  const normalizedLabel = label.toLowerCase().trim();
  const normalizedSublabel = sublabel.toLowerCase().trim();
  const normalizedQuery = query.toLowerCase().trim();
  const combined = `${normalizedLabel} ${normalizedSublabel}`;

  // Exact match on label
  if (normalizedLabel === normalizedQuery) return 1.0;

  // Label starts with query (e.g., "1977 main st" matches "1977 main street")
  if (normalizedLabel.startsWith(normalizedQuery)) return 0.9;

  // Query starts with label (query is more specific than label)
  if (normalizedQuery.startsWith(normalizedLabel)) return 0.8;

  // Word-based matching
  const queryWords = normalizedQuery.split(/\s+/).filter(w => w.length > 0);
  if (queryWords.length === 0) return 0;

  const matchedWords = queryWords.filter(
    word => combined.includes(word) || normalizedLabel.includes(word),
  );

  const matchRatio = matchedWords.length / queryWords.length;

  // All query words found
  if (matchRatio === 1) return 0.7;

  // Partial word match (scale from 0.1 to 0.5 based on ratio)
  if (matchRatio > 0) return 0.1 + matchRatio * 0.4;

  return 0;
}

/**
 * Turn a dotted attribute path (`officer.full_name`, `address`) into a
 * human-readable label (`Officer Full Name`, `Address`). Underscores and
 * dots are treated as word boundaries; each word is capitalized.
 */
function humanizeFieldPath(path: string): string {
  if (!path) return '';
  return path
    .split('.')
    .map(seg =>
      seg
        .split(/[_\s]+/)
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' '),
    )
    .filter(Boolean)
    .join(' ');
}

/** Product label for IaR/dynamic fields in the results sublabel. */
function formatSpatialMatchedFieldLabel(path: string): string {
  const lower = path.toLowerCase();
  if (
    lower === 'unit' ||
    lower === 'unit_title' ||
    lower.endsWith('apparatusname')
  ) {
    return 'UNIT';
  }
  if (lower === 'member' || lower.includes('membername')) {
    return 'Member';
  }
  if (lower.endsWith('memberposition')) {
    return 'Position';
  }
  return humanizeFieldPath(path);
}

function pickDynamicUnitTitle(
  spatialObj: Record<string, unknown>,
  spatialAttrs: Record<string, unknown>,
  pickStr: (v: unknown) => string | undefined,
): string | undefined {
  const vehicleDetails = spatialAttrs.vehicle_details as
    | Record<string, unknown>
    | undefined;
  return (
    pickStr(spatialObj.header) ||
    pickStr(spatialObj.unit) ||
    pickStr(spatialObj.unit_id) ||
    pickStr(spatialObj.unit_title) ||
    pickStr(spatialAttrs.unit) ||
    pickStr(spatialAttrs.unit_id) ||
    pickStr(spatialAttrs.unit_title) ||
    pickStr(vehicleDetails?.ApparatusName) ||
    pickStr(vehicleDetails?.ApparatusAbbreviation)
  );
}

function pickDynamicResponderLabel(
  spatialAttrs: Record<string, unknown>,
  pickStr: (v: unknown) => string | undefined,
): string | undefined {
  const responderDetails = spatialAttrs.responder_details as
    | Record<string, unknown>
    | undefined;
  if (!responderDetails) return undefined;
  const first = pickStr(responderDetails.MemberName);
  const last = pickStr(responderDetails.MemberName_last);
  const fullName = [first, last].filter(Boolean).join(' ');
  return fullName || first || last;
}

type IarRowKind = 'vehicle' | 'responder';

function detectIarRow(
  spatialObj: Record<string, unknown>,
  spatialAttrs: Record<string, unknown>,
  pickStr: (v: unknown) => string | undefined,
): { kind: IarRowKind; headline: string } | null {
  const unitTitle = pickDynamicUnitTitle(spatialObj, spatialAttrs, pickStr);
  if (unitTitle) {
    return { kind: 'vehicle', headline: unitTitle };
  }
  const memberLabel = pickDynamicResponderLabel(spatialAttrs, pickStr);
  if (memberLabel) {
    return { kind: 'responder', headline: memberLabel };
  }
  return null;
}

const JSON_SCHEMA_TYPES = new Set([
  'string',
  'number',
  'integer',
  'boolean',
  'object',
  'array',
  'null',
]);

function isDisplaySchemaProperty(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.type === 'string' &&
    JSON_SCHEMA_TYPES.has(obj.type) &&
    typeof obj.title === 'string'
  );
}

/** IaR vehicle_details keys aligned with the layer display-schema titles. */
const IAR_VEHICLE_DETAIL_FIELDS: { key: string; label: string }[] = [
  { key: 'RespondingTo', label: 'Status' },
  { key: 'AgencyName', label: 'Agency' },
  { key: 'ApparatusTypeName', label: 'Type' },
  { key: 'MakeAndModel', label: 'Make & Model' },
  { key: 'ApparatusCategoryName', label: 'Description' },
];

const IAR_RESPONDER_DETAIL_FIELDS: { key: string; label: string }[] = [
  { key: 'MemberPosition', label: 'Position' },
  { key: 'AgencyName', label: 'Agency' },
  { key: 'RespondingTo', label: 'Status' },
];

const SKIPPED_SPATIAL_ATTR_KEYS = new Set([
  'display',
  'vehicle_details',
  'responder_details',
  'display_schema',
  '$schema',
  'properties',
  'propertiesOrder',
]);

function appendIarVehicleDetails(
  parts: string[],
  vehicleDetails: Record<string, unknown> | undefined,
  pickStr: (v: unknown) => string | undefined,
): void {
  if (!vehicleDetails) return;
  for (const { key, label } of IAR_VEHICLE_DETAIL_FIELDS) {
    const value = pickStr(vehicleDetails[key]);
    if (!value) continue;
    pushSublabelPart(parts, `${label}: ${value}`);
  }
}

function appendIarResponderDetails(
  parts: string[],
  responderDetails: Record<string, unknown> | undefined,
  pickStr: (v: unknown) => string | undefined,
): void {
  if (!responderDetails) return;
  for (const { key, label } of IAR_RESPONDER_DETAIL_FIELDS) {
    const value = pickStr(responderDetails[key]);
    if (!value) continue;
    pushSublabelPart(parts, `${label}: ${value}`);
  }
}

function appendGenericSpatialAttrs(
  parts: string[],
  attrs: Record<string, unknown>,
  pickStr: (v: unknown) => string | undefined,
  opts?: { skipValues?: Set<string> },
): void {
  const skipValues = opts?.skipValues ?? new Set<string>();
  for (const [key, value] of Object.entries(attrs)) {
    if (SKIPPED_SPATIAL_ATTR_KEYS.has(key)) continue;
    if (isDisplaySchemaProperty(value)) continue;

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      const text =
        pickStr(value) ??
        (typeof value === 'boolean' ? String(value) : undefined);
      if (text && !skipValues.has(text)) {
        pushSublabelPart(parts, `${humanizeFieldPath(key)}: ${text}`);
      }
      continue;
    }

    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;

    for (const [nestedKey, nestedValue] of Object.entries(
      value as Record<string, unknown>,
    )) {
      if (isDisplaySchemaProperty(nestedValue)) continue;
      const text = pickStr(nestedValue);
      if (!text || skipValues.has(text)) continue;
      const fieldPath = `${key}.${nestedKey}`;
      pushSublabelPart(
        parts,
        `${formatSpatialMatchedFieldLabel(fieldPath)}: ${text}`,
      );
    }
  }
}

export interface SpatialLayerResultDisplay {
  label: string;
  sublabelParts: string[];
}

/** Build dropdown label/sublabel for static and dynamic spatial-layer hits. */
export function buildSpatialLayerResultDisplay(args: {
  itemType: 'static' | 'dynamic';
  result: {
    layerKey?: string;
    layerTitle?: string;
    layerSource?: string;
    matchedField?: string;
    matchedText?: string;
  };
  spatialObj: Record<string, unknown>;
  spatialAttrs: Record<string, unknown>;
}): SpatialLayerResultDisplay {
  const { itemType, result, spatialObj, spatialAttrs } = args;
  const pickStr = (v: unknown): string | undefined =>
    typeof v === 'string' && v.trim() ? v : undefined;

  const iarRow =
    itemType === 'dynamic'
      ? detectIarRow(spatialObj, spatialAttrs, pickStr)
      : null;
  const subParts: string[] = [];
  const skipValues = new Set<string>();

  if (iarRow?.kind === 'vehicle') {
    skipValues.add(iarRow.headline);
    const label = `UNIT: ${iarRow.headline}`;
    pushSublabelPart(subParts, formatSpatialSourceLine(result));
    appendIarVehicleDetails(
      subParts,
      spatialAttrs.vehicle_details as Record<string, unknown> | undefined,
      pickStr,
    );
    appendGenericSpatialAttrs(subParts, spatialAttrs, pickStr, { skipValues });

    const addr =
      pickStr(spatialAttrs.address) || pickStr(spatialAttrs.location);
    pushSublabelPart(subParts, addr);

    return { label, sublabelParts: subParts };
  }

  if (iarRow?.kind === 'responder') {
    skipValues.add(iarRow.headline);
    const label = `Member: ${iarRow.headline}`;
    pushSublabelPart(subParts, formatSpatialSourceLine(result));
    appendIarResponderDetails(
      subParts,
      spatialAttrs.responder_details as Record<string, unknown> | undefined,
      pickStr,
    );
    appendGenericSpatialAttrs(subParts, spatialAttrs, pickStr, { skipValues });

    const addr =
      pickStr(spatialAttrs.address) || pickStr(spatialAttrs.location);
    pushSublabelPart(subParts, addr);

    return { label, sublabelParts: subParts };
  }

  const label =
    pickStr(spatialObj.name) ||
    pickStr(spatialObj.title) ||
    pickStr(spatialAttrs.title) ||
    pickStr(spatialAttrs.name) ||
    pickStr(spatialAttrs.full_name) ||
    pickStr(spatialAttrs.label) ||
    result.matchedText ||
    result.layerTitle;

  if (label) skipValues.add(label);
  pushSublabelPart(subParts, formatSpatialSourceLine(result));

  if (
    result.matchedField &&
    result.matchedText &&
    result.matchedText !== label
  ) {
    const fieldLabel = formatSpatialMatchedFieldLabel(result.matchedField);
    pushSublabelPart(subParts, `${fieldLabel}: ${result.matchedText}`);
    skipValues.add(result.matchedText);
  }

  appendGenericSpatialAttrs(subParts, spatialAttrs, pickStr, { skipValues });

  const addr = pickStr(spatialAttrs.address) || pickStr(spatialAttrs.location);
  pushSublabelPart(subParts, addr);

  return {
    label: label || result.layerTitle || 'Layer',
    sublabelParts: subParts,
  };
}

function pushSublabelPart(parts: string[], value?: string): void {
  const trimmed = value?.trim();
  if (trimmed && !parts.includes(trimmed)) {
    parts.push(trimmed);
  }
}

function formatSpatialSourceLine(result: {
  layerKey?: string;
  layerTitle?: string;
  layerSource?: string;
}): string | undefined {
  const value =
    result.layerKey?.trim() ||
    result.layerTitle?.trim() ||
    result.layerSource?.trim();
  return value ? `source: ${value}` : undefined;
}

/** Determine which display category a single result item belongs to. */
function normalizeServiceUrl(url?: string): string {
  return (url || '').trim().toLowerCase().replace(/\/+$/, '');
}

function categoryFor(item: SearchResultItem): string {
  if (item.type === 'axon') return 'axon';
  if (item.type === 'place') {
    // what3words hits ride the `place` result type (same row + pin
    // behavior) but get their own category so they surface under a
    // dedicated filter pill instead of mixing into addresses.
    if (item.data.attributes?.Addr_type === 'what3words') return 'what3words';
    const source = normalizeServiceUrl(item.data._serviceUrl);
    if (!source) return 'place';
    if (source === RSOS_PLACE_SOURCE) return 'place-rsos';
    return isGlobalPlaceService(source) ? 'place-global' : 'place-local';
  }
  if (item.type === 'static') return 'static';
  if (item.type === 'dynamic') return 'dynamic';
  if (item.type === 'call') return 'call';
  if (item.type === 'transcript') return 'transcript';
  if (item.type === 'message') return 'message';
  if (item.type === 'alert') return 'alert';
  // RAD-e (MAF + ADR) hits share a single visual category so the
  // dropdown only ever renders one "Additional Data" section even
  // when both lookups resolved with data.
  if (item.type === 'rade-maf') return 'rade-maf';
  if (item.type === 'rade-adr') return 'rade-adr';
  const kind = item.data.kind;
  if (kind === 'hydrant' || kind === 'parcel') return kind;
  return (item.data.attributes?._sourceLayer as string) || kind;
}

/** Build a PreparedItem for a single result, including distance from map center. */
function prepareItem(
  item: SearchResultItem,
  mapCenter: MapCenter,
  center: { latitude: number; longitude: number } | null,
): PreparedItem {
  if (item.type === 'axon') {
    const officer = item.data.attributes?.officer;
    const label = officer?.full_name || officer?.last_name || 'Axon Officer';
    const parts: string[] = [];
    const badge = officer?.badge_number;
    if (badge) parts.push(`Badge: ${badge}`);
    let dist = Infinity;
    let distLabel = '';
    if (center) {
      const coords = item.data.shape?.features?.[0]?.geometry?.coordinates;
      if (coords?.[0] != null && coords?.[1] != null) {
        dist = haversineDistance(
          center.latitude,
          center.longitude,
          coords[1],
          coords[0],
        );
        distLabel = formatDistance(dist);
      }
    }
    return {
      item,
      label,
      sublabel: parts.join('  ·  '),
      distanceLabel: distLabel,
      flatIdx: 0,
      icon: 'axon',
      distance: dist,
      textMatchScore: 0,
    };
  }

  if (item.type === 'place') {
    const attrs = item.data.attributes || {};
    const isW3W = attrs.Addr_type === 'what3words';
    const shortLabel = attrs.ShortLabel as string | undefined;
    const placeAddr = attrs.Place_addr as string | undefined;
    const placeName = attrs.PlaceName as string | undefined;
    // ESRI's POI taxonomy lives in the `Type` attribute (e.g.
    // "Hospital", "Gas Station", "Hotel"). Most plain street-address
    // results leave it empty, which is exactly what we want — those
    // rows should keep their default 'place' icon.
    const placeType = attrs.Type as string | undefined;
    const addr = item.data.address;

    const placeLabel =
      shortLabel ||
      (addr.indexOf(',') > 0 ? addr.slice(0, addr.indexOf(',')) : addr);

    const parts: string[] = [];
    if (placeAddr) {
      parts.push(placeAddr);
    } else {
      const afterComma =
        addr.indexOf(',') > 0 ? addr.slice(addr.indexOf(',') + 1).trim() : '';
      if (afterComma) parts.push(afterComma);
    }

    let dist = Infinity;
    let distLabel = '';
    if (center && item.data.location) {
      dist = haversineDistance(
        center.latitude,
        center.longitude,
        item.data.location.y,
        item.data.location.x,
      );
      distLabel = formatDistance(dist);
    }
    return {
      item,
      label: placeLabel,
      sublabel: parts.join('  ·  '),
      distanceLabel: distLabel,
      flatIdx: 0,
      icon: isW3W ? 'what3words' : 'place',
      // Resolve a category-specific silhouette for POIs while leaving
      // pure street-address rows on the default 'place' icon. We never
      // pass the raw address into the resolver — that risks false
      // positives like "Park Avenue" landing the park icon. W3W rows
      // use their own glyph, so skip the silhouette resolver entirely.
      iconUrl: isW3W ? undefined : resolveCategoryIcon(placeType, placeName),
      distance: dist,
      textMatchScore: 0,
    };
  }

  if (item.type === 'static' || item.type === 'dynamic') {
    const result = item.data;
    const spatialObj = result.spatialObject as Record<string, unknown>;
    const spatialAttrs = (result.spatialObject.attributes || {}) as Record<
      string,
      unknown
    >;
    const pickStr = (v: unknown): string | undefined =>
      typeof v === 'string' && v.trim() ? v : undefined;

    const { label, sublabelParts } = buildSpatialLayerResultDisplay({
      itemType: item.type,
      result,
      spatialObj,
      spatialAttrs,
    });

    let dist = Infinity;
    let distLabel = '';
    const objCenter = getSpatialObjectCenter(result.spatialObject);
    if (center && objCenter) {
      dist = haversineDistance(
        center.latitude,
        center.longitude,
        objCenter.latitude,
        objCenter.longitude,
      );
      distLabel = formatDistance(dist);
    }

    return {
      item,
      label,
      sublabel: sublabelParts.join('  ·  '),
      distanceLabel: distLabel,
      flatIdx: 0,
      icon: item.type,
      iconUrl: resolveCategoryIcon(
        result.layerTitle,
        pickStr(spatialObj.name) || pickStr(spatialObj.title),
      ),
      distance: dist,
      textMatchScore: 0,
    };
  }

  if (item.type === 'transcript') {
    const { callerId, speakerLabel, matchedText } = item.data;
    const label =
      formatInternationalPhoneNumberHuman(callerId) || 'Active Call';
    const sublabel = speakerLabel
      ? `${speakerLabel}: "${matchedText}"`
      : `"${matchedText}"`;
    return {
      item,
      label,
      sublabel,
      distanceLabel: '',
      flatIdx: 0,
      icon: 'transcript',
      // Transcript matches don't carry a centroid; sort to the bottom of the
      // bucket consistently (matches the pattern used for `call` rows).
      distance: Infinity,
      textMatchScore: 0,
    };
  }

  if (item.type === 'message') {
    const { callerId, source, matchedText } = item.data;
    const label =
      formatInternationalPhoneNumberHuman(callerId) || 'Active Call';
    // Surface which conversation surface the hit came from so the user
    // can tell e.g. an agency-share thread apart from a TF911 chat.
    const sourceLabel = MESSAGE_SOURCE_LABELS[source] || 'Message';
    const sublabel = `${sourceLabel}: "${matchedText}"`;
    return {
      item,
      label,
      sublabel,
      distanceLabel: '',
      flatIdx: 0,
      icon: 'message',
      distance: Infinity,
      textMatchScore: 0,
    };
  }

  if (item.type === 'alert') {
    const { alert, matchedField, matchedText } = item.data;
    const label =
      alert.emergency_type?.display_name ||
      alert.emergency_type?.name ||
      alert.alert_id;

    // Sublabel format: status · matched-field-context · address. Skip
    // any segment that's blank or that would just echo the headline.
    const subParts: string[] = [];
    const statusLabel = alert.status?.display_name || alert.status?.name || '';
    if (statusLabel && statusLabel !== label) subParts.push(statusLabel);

    if (matchedField && matchedText && matchedText !== label) {
      // Match-field labels from `runAlertsSearch` are already human-
      // readable, so we skip the usual `humanizeFieldPath` pass.
      subParts.push(`${matchedField}: ${matchedText}`);
    }

    const civic =
      alert.location && 'civic' in alert.location ? alert.location.civic : null;
    const addressParts: string[] = [];
    if (civic?.street_1) addressParts.push(civic.street_1);
    if (civic?.city) addressParts.push(civic.city);
    const address = addressParts.join(', ');
    if (address && !subParts.some(p => p.includes(address))) {
      subParts.push(address);
    }

    let dist = Infinity;
    let distLabel = '';
    const geodetic =
      alert.location && 'geodetic' in alert.location
        ? alert.location.geodetic
        : null;
    if (
      center &&
      geodetic &&
      geodetic.latitude != null &&
      geodetic.longitude != null
    ) {
      dist = haversineDistance(
        center.latitude,
        center.longitude,
        geodetic.latitude,
        geodetic.longitude,
      );
      distLabel = formatDistance(dist);
    }

    return {
      item,
      label,
      sublabel: subParts.join('  ·  '),
      distanceLabel: distLabel,
      flatIdx: 0,
      icon: 'alert',
      distance: dist,
      textMatchScore: 0,
    };
  }

  if (item.type === 'rade-maf') {
    return {
      item,
      label: `MedicAlert profile ${item.data.id}`,
      sublabel: 'View additional data',
      distanceLabel: '',
      flatIdx: 0,
      icon: 'rade-maf',
      // RAD-e rows are not geo-anchored; sort to the bottom of the
      // bucket consistently rather than fight with the place sorter.
      distance: Infinity,
      textMatchScore: 0,
    };
  }

  if (item.type === 'rade-adr') {
    const formatted =
      formatInternationalPhoneNumberHuman(item.data.id) || item.data.id;
    return {
      item,
      label: formatted,
      sublabel: 'Additional data lookup',
      distanceLabel: '',
      flatIdx: 0,
      icon: 'rade-adr',
      distance: Infinity,
      textMatchScore: 0,
    };
  }

  if (item.type === 'call') {
    const { callerId, matchedField, matchedText } = item.data;
    const label =
      formatInternationalPhoneNumberHuman(callerId) || 'Active Call';

    // When the hit came from inside the call's ADR, surface the matched
    // field so the user can tell why this call came back. Otherwise fall
    // back to the generic descriptor.
    let sublabel = 'Active 911 call';
    if (matchedField && matchedText) {
      const humanField = matchedField
        .split(' · ')
        .map(seg =>
          seg.includes('.')
            ? `${seg.split('.')[0]} · ${humanizeFieldPath(
                seg.split('.').slice(1).join('.'),
              )}`
            : seg,
        )
        .join(' · ');
      sublabel = `${humanField}: ${matchedText}`;
    }

    return {
      item,
      label,
      sublabel,
      distanceLabel: '',
      flatIdx: 0,
      icon: 'call',
      // Calls don't carry a centroid here; treat them as "no distance" so
      // they sort to the bottom of the bucket consistently.
      distance: Infinity,
      textMatchScore: 0,
    };
  }

  const attrs = (item.data.attributes || {}) as Record<string, unknown>;
  const label = getFeatureLabel(attrs, item.data.kind);
  const sublabel = getFeatureSublabel(
    attrs,
    item.data.kind,
    item.data.geometry,
    mapCenter,
  );
  let dist = Infinity;
  let distLabel = '';
  if (center && item.data.geometry?.centroid) {
    dist = haversineDistance(
      center.latitude,
      center.longitude,
      item.data.geometry.centroid.latitude,
      item.data.geometry.centroid.longitude,
    );
    distLabel = formatDistance(dist);
  }
  // For generic GIS hits the originating layer title (`sourceTitle` /
  // `_sourceLayer`) is the strongest category cue. Falling back to
  // `kind` covers the dedicated `hydrant` / `parcel` buckets without
  // any extra branching since their kind strings are themselves
  // category keywords.
  const sourceLayer =
    typeof attrs._sourceLayer === 'string'
      ? (attrs._sourceLayer as string)
      : undefined;
  return {
    item,
    label,
    sublabel,
    distanceLabel: distLabel,
    flatIdx: 0,
    icon: 'gis',
    iconUrl: resolveCategoryIcon(
      item.data.sourceTitle,
      sourceLayer,
      item.data.kind,
    ),
    distance: dist,
    textMatchScore: 0,
  };
}

export interface GroupedResults {
  groups: ResultGroup[];
  counts: Record<string, number>;
  categoryOrder: string[];
  availableCategories: string[];
  visibleItems: PreparedItem[];
}

/**
 * Bucket raw search results into ordered, deduplicated, distance-sorted
 * categories ready for rendering. Maintains the order categories first
 * appeared in the result stream so the UI doesn't reshuffle as new sources
 * stream in.
 */
/**
 * Lookup index for the configured priority list. Lower index = renders
 * first; categories absent from the list fall back to first-seen order
 * below all listed buckets.
 */
const PRIORITY_INDEX: Record<string, number> = (() => {
  const map: Record<string, number> = {};
  CATEGORY_PRIORITY_ORDER.forEach((cat, idx) => {
    map[cat] = idx;
  });
  return map;
})();

const UNRANKED_PRIORITY = Number.MAX_SAFE_INTEGER;

export function getCategoryOrderForResults(
  results: SearchResultItem[],
): string[] {
  // Track first-seen order so unranked categories keep a stable
  // ordering between renders (and so equally-ranked-but-unranked
  // buckets don't reshuffle as new sources stream in).
  const firstSeen = new Map<string, number>();
  for (const item of results) {
    const cat = categoryFor(item);
    if (!firstSeen.has(cat)) {
      firstSeen.set(cat, firstSeen.size);
    }
  }
  // Stable sort: priority bucket first (calls -> axon/dynamic ->
  // address/location -> parcel -> text -> ADR), then everything else
  // in the order it streamed in.
  return Array.from(firstSeen.keys()).sort((a, b) => {
    const pa = PRIORITY_INDEX[a] ?? UNRANKED_PRIORITY;
    const pb = PRIORITY_INDEX[b] ?? UNRANKED_PRIORITY;
    if (pa !== pb) return pa - pb;
    return (firstSeen.get(a) ?? 0) - (firstSeen.get(b) ?? 0);
  });
}

/** RapidSOS and global (Esri, Google) places share one MAX_POI cap. */
const GLOBAL_PLACE_CATEGORIES = new Set(['place-rsos', 'place-global']);

/**
 * Drop repeated places and keep global places inside the same cap as
 * the rest of the geocoder list. The same street and building number
 * is shown again when the city, state, or direction differs. A
 * different building number on that street is omitted. Earlier rows win.
 */
export function selectVisibleSearchResults(
  results: SearchResultItem[],
): SearchResultItem[] {
  const seenAddresses = new PlaceResultDedupe();
  let globalPlaces = 0;
  const visible: SearchResultItem[] = [];

  for (const item of results) {
    if (item.type === 'place') {
      const isGlobal = GLOBAL_PLACE_CATEGORIES.has(categoryFor(item));
      if (isGlobal && globalPlaces >= MAX_POI) continue;
      if (!seenAddresses.accept(item.data)) continue;
      if (isGlobal) globalPlaces += 1;
    }
    visible.push(item);
  }

  return visible;
}

export default function useGroupedResults(
  results: SearchResultItem[],
  mapCenter: MapCenter,
  activeFilter: string | null,
  query = '',
): GroupedResults {
  const visibleResults = useMemo(
    () => selectVisibleSearchResults(results),
    [results],
  );
  const categoryOrder = useMemo(() => {
    return getCategoryOrderForResults(visibleResults);
  }, [visibleResults]);

  const grouped = useMemo(() => {
    const buckets: Record<string, PreparedItem[]> = {};
    const ensureBucket = (cat: string): PreparedItem[] => {
      if (!buckets[cat]) buckets[cat] = [];
      return buckets[cat];
    };

    const center =
      mapCenter?.latitude != null && mapCenter?.longitude != null
        ? { latitude: mapCenter.latitude, longitude: mapCenter.longitude }
        : null;

    for (const item of visibleResults) {
      const cat = categoryFor(item);
      const prepared = prepareItem(item, mapCenter, center);
      // Calculate text match score for ranking within category
      prepared.textMatchScore = calculateTextMatchScore(
        prepared.label,
        prepared.sublabel,
        query,
      );
      ensureBucket(cat).push(prepared);
    }

    // Deduplicate across GIS buckets: if the same label+sublabel appears
    // in multiple sublayer categories, keep only the first occurrence.
    const seen = new Set<string>();
    const NON_GIS_CATEGORIES = new Set([
      'axon',
      'place',
      'place-local',
      'place-rsos',
      'place-global',
      'what3words',
      'static',
      'dynamic',
      'call',
      'transcript',
      'message',
      'alert',
      'rade-maf',
      'rade-adr',
    ]);
    for (const cat of categoryOrder) {
      const bucket = buckets[cat];
      if (!bucket) continue;
      if (NON_GIS_CATEGORIES.has(cat)) continue;
      buckets[cat] = bucket.filter(p => {
        const key = `${p.label}\0${p.sublabel}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    const groups: ResultGroup[] = [];
    const counts: Record<string, number> = {};
    let runningIdx = 0;
    for (const cat of categoryOrder) {
      const bucket = buckets[cat];
      if (bucket && bucket.length > 0) {
        // Section order is the filter-badge order. Within a badge,
        // prioritize better text matches first, then sort by distance.
        bucket.sort((a, b) => {
          // Higher text match score = better match, so sort descending
          const scoreDiff = b.textMatchScore - a.textMatchScore;
          // If scores are significantly different (>0.1), use score
          if (Math.abs(scoreDiff) > 0.1) return scoreDiff;
          // Otherwise fall back to distance (ascending)
          return a.distance - b.distance;
        });
        counts[cat] = bucket.length;
        if (activeFilter && activeFilter !== cat) continue;
        for (const prepared of bucket) {
          prepared.flatIdx = runningIdx++;
        }
        groups.push({
          category: cat,
          heading: FIXED_CATEGORY_LABELS[cat] || cat,
          items: bucket,
        });
      }
    }
    return { groups, counts };
  }, [visibleResults, mapCenter, activeFilter, categoryOrder, query]);

  const availableCategories = useMemo(
    () => categoryOrder.filter(cat => (grouped.counts[cat] ?? 0) > 0),
    [categoryOrder, grouped.counts],
  );

  const visibleItems = useMemo(
    () => grouped.groups.flatMap(g => g.items),
    [grouped],
  );

  return {
    groups: grouped.groups,
    counts: grouped.counts,
    categoryOrder,
    availableCategories,
    visibleItems,
  };
}
